import Col from 'antd/es/col'
import Form from 'antd/es/form'
import InputNumber from 'antd/es/input-number'
import message from 'antd/es/message'
import notification from 'antd/es/notification'
import Row from 'antd/es/row'
import Select from 'antd/es/select'
import TreeSelect from 'antd/es/tree-select'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../../features/setting/setting.slice.ts'
import { convertPxToNumber } from '../../../../../utils/helpers.ts'
import IdeyaTypography from '../../../../../common/antd/ideya-typography.tsx'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import './action-idea-form.scss'
import { IdeyaButton } from '../../../../../common/antd/ideya-button/ideya-button.tsx'
import { Checkbox, FormInstance, FormProps } from 'antd/lib/index'
import Radio from 'antd/es/radio/index'
import {
  useGetTickerShortableQuery,
  useLazyGetTickerPriceQuery,
  useLazySearchTickerQuery,
} from '../../../../../apis/ticker/ticker-api.api.ts'
import { debounce } from 'lodash'
import {
  FillStrategies,
  TickerOption,
  TradeReasonsTreeData,
} from './action-idea-form.ts'
import { UserInfo } from '../../../../../apis/auth/auth.def.ts'
import {
  useCheckFillStrategyQuery,
  useCheckIdeaExistQuery,
  useCreateIdeaMutation,
  useUpdateIdeaMutation,
} from '../../../../../apis/idea/idea-api.api.ts'
import { getErrorDescription } from '../../../../../redux/utils.ts'
import {
  IdeaDetail,
  IdeaUpdateRequest,
} from '../../../../../apis/idea/idea-api.def.ts'
import { RuleObject } from 'antd/es/form/index'
import { useIsMobile } from '../../../../../common/hooks/use-is-mobile.ts'
import { useTranslation } from 'react-i18next'
import { InvalidTickerMessage } from './invalid-ticker-message.tsx'
import { skipToken } from '@reduxjs/toolkit/query'
import { NEW_RELIC, newRelic } from '../../../../../utils/new-relic.ts'
import Switch from 'antd/es/switch/index'
import { IdeaDirectionType } from '../../../../../enums/idea-list.enum.ts'
import theme from 'antd/es/theme/index'
import './action-idea-form.scss'

// Transition MS Antd Segmented Thumb (Animation on changed selection)
const THUMB_TRANSITION_MS = 200
const MAX_TRADE_CAP = 5_000_000

const { useToken } = theme

function normalizeTradeReasonValue(value?: string | string[]): string {
  const reasonList = Array.isArray(value)
    ? value
    : (value ?? '').split(',').map((reason) => reason.trim())

  return reasonList
    .map((reason) => reason.trim())
    .filter(Boolean)
    .sort()
    .join(',')
}

export type CreateIdeaFormFieldTypes = {
  figi: string
  direction: IdeaDirectionType | ''
  targetMarketValue: number
  target: number
  stop: number
  fillStrategy: string
  tradeReason: string
  horizon: string
  conviction: string
  comment: string
  keepFilling: boolean
}

interface CreateIdeaFormProps {
  onCancel: () => void
  form: FormInstance<CreateIdeaFormFieldTypes>
  buyingPower?: number
  usedBuyingPower?: number
  userInfo: UserInfo
  onIdeaActioned?: () => void
  mode?: 'create' | 'edit'
  ideaDetail?: IdeaDetail
  onEditIdea?: (ideaId: string) => void
  onIdeaLiquidated: () => void
}

export interface CreateIdeaFormRef {
  resetFormState: () => void
}

export const ActionIdeaForm = forwardRef<
  CreateIdeaFormRef,
  CreateIdeaFormProps
>(
  (
    {
      onCancel,
      form,
      buyingPower,
      usedBuyingPower,
      userInfo,
      onIdeaActioned,
      ideaDetail,
      mode = 'create',
      onEditIdea,
      onIdeaLiquidated,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const themeStyle = useAppSelector(selectThemeStyle)
    const { token } = useToken()
    const targetSectionBgColor = {
      Long: themeStyle.green1,
      Short: themeStyle.red1,
      '': token.colorBgLayout,
    }

    const [visualDirection, setVisualDirection] = useState<
      IdeaDirectionType | ''
    >('')
    const [isMaxFillReached, setIsMaxFillReached] = useState<boolean>(false)
    const [horizonValue, setHorizonValue] = useState<string>(
      ideaDetail?.horizon ?? ''
    )
    const [convictionValue, setConvictionValue] = useState<string>(
      ideaDetail?.conviction ?? ''
    )
    const [tickerOptions, setTickerOptions] = useState<TickerOption[]>([])
    const [companyName, setCompanyName] = useState<string>('')
    const [maxTargetMarketValue, setMaxTargetMarketValue] =
      useState<number>(MAX_TRADE_CAP)
    const [selectedBbgCompositeKey, setSelectedBbgCompositeKey] =
      useState<string>(ideaDetail?.bbgCompositeKey ?? '')
    const [selectedFigi, setSelectedFigi] = useState<string>('')
    // For mobile only
    const [selectedTradeReasons, setSelectedTradeReasons] = useState<string[]>(
      []
    )
    const [showCannotShortMessage, setShowCannotShortMessage] =
      useState<boolean>(false)
    const [messageApi, messageContextHolder] = message.useMessage()
    const [notificationApi, notificationContextHolder] =
      notification.useNotification()
    const [directionError, setDirectionError] = useState<string>('')
    const isGlobal = userInfo.region === 'Global'

    const MinTargetMarketValue = isGlobal ? 0 : 600000
    const TargetMarketValueStep = 100000
    const availableCapital = (
      (buyingPower ?? 0) - (usedBuyingPower ?? 0)
    ).toLocaleString(undefined, { maximumFractionDigits: 0 })

    // Special check for JP and Global users (Different BP value buttons)

    const isJpOrGlobal = userInfo.region === 'Japan' || isGlobal

    const { data: fillStrategyData, isLoading: fillStrategyLoading } =
      useCheckFillStrategyQuery(
        ideaDetail
          ? {
              id: ideaDetail.id,
              fillStrategy: ideaDetail.fillStrategy,
            }
          : skipToken,
        {
          refetchOnMountOrArgChange: true,
          skip: !ideaDetail,
        }
      )

    const [fetchTickerSearch, { data: tickerData, isLoading: tickerLoading }] =
      useLazySearchTickerQuery()

    const [fetchTickerPrice, { data: tickerPriceData }] =
      useLazyGetTickerPriceQuery()

    const [
      createIdea,
      { isLoading: isCreating, isSuccess, isError, error, reset },
    ] = useCreateIdeaMutation()

    const [
      updateIdea,
      {
        isLoading: isUpdateLoading,
        isSuccess: isUpdateSuccess,
        isError: isUpdateError,
        reset: updateReset,
      },
    ] = useUpdateIdeaMutation()

    const { data: isShortableData, isLoading: isShortableLoading } =
      useGetTickerShortableQuery(
        { bbgCompositeKey: selectedBbgCompositeKey },
        {
          skip: !selectedBbgCompositeKey,
          refetchOnMountOrArgChange: true,
          refetchOnReconnect: true,
        }
      )

    const {
      currentData: checkIdeaExistData,
      isLoading: checkIdeaExistLoading,
    } = useCheckIdeaExistQuery({ figi: selectedFigi }, { skip: !selectedFigi })

    useEffect(() => {
      if (ideaDetail) {
        const tickerOption: TickerOption = {
          label: ideaDetail.bbgCompositeKey,
          bbgCompositeKey: ideaDetail.bbgCompositeKey,
          name: ideaDetail.ticker.name,
          legalName: ideaDetail.ticker.legalName,
          value: ideaDetail.ticker.figi,
          status: ideaDetail.ticker.status,
        }

        fetchTickerSearch({ keyword: ideaDetail.ticker.figi })
        fetchTickerPrice({ bbgCompositeKey: ideaDetail.ticker.bbgCompositeKey })
        setSelectedBbgCompositeKey(ideaDetail.ticker.bbgCompositeKey)

        setTickerOptions([tickerOption])
        let finalCompanyName = ideaDetail.ticker.name
        if (ideaDetail.ticker.legalName) {
          finalCompanyName = finalCompanyName.concat(
            ' (' + ideaDetail.ticker.legalName + ')'
          )
        }
        setCompanyName(finalCompanyName)
        setHorizonValue(ideaDetail.horizon)
        setConvictionValue(ideaDetail.conviction)
        setVisualDirection(ideaDetail.direction as IdeaDirectionType)

        const tradeReasonsArray = ideaDetail.tradeReason
          .split(',')
          .map((reason) => reason.trim())
        setSelectedTradeReasons(tradeReasonsArray)
        form.setFieldsValue({
          tradeReason: tradeReasonsArray as unknown as string,
          direction: ideaDetail.direction as IdeaDirectionType,
        })
      }
    }, [fetchTickerPrice, fetchTickerSearch, ideaDetail, isShortableData])

    // Edit-mode target market value cap: never below the existing position so it
    // can always be reduced; small ideas keep the 5M allowance.
    useEffect(() => {
      if (!ideaDetail || isGlobal) return

      // Short on a non-shortable ticker: can't add more, cap at existing size.
      if (
        isShortableData &&
        fillStrategyData &&
        !isShortableData.data.isShortable &&
        ideaDetail.direction === IdeaDirectionType.Short
      ) {
        setMaxTargetMarketValue(
          fillStrategyData.data.hasUnfilledAction
            ? ideaDetail.targetMarketValue
            : ideaDetail.currentMarketValue
        )
        return
      }

      const maxUpSize = ideaDetail.ticker?.maxUpSize
        ? parseFloat(ideaDetail.ticker.maxUpSize)
        : 0
      setMaxTargetMarketValue(
        Math.round(Math.max(
          Math.min(ideaDetail.currentMarketValue + maxUpSize, MAX_TRADE_CAP),
          ideaDetail.currentMarketValue
        ))
      )
    }, [ideaDetail, isGlobal, isShortableData, fillStrategyData])

    useEffect(() => {
      if (mode === 'create' && isGlobal && buyingPower) {
        setMaxTargetMarketValue(buyingPower)
      }
    }, [mode, userInfo.region, buyingPower])

    // Update ticker options when API data changes
    useEffect(() => {
      if (tickerData?.data?.tickers) {
        setTickerOptions(
          tickerData.data.tickers.map((t) => ({
            key: t.figi,
            value: t.figi,
            label: `${t.tickerCode} - ${t.name}`,
            bbgCompositeKey: t.bbgCompositeKey,
            name: t.name,
            legalName: t.legalName,
            status: t.status,
          }))
        )
      }
    }, [tickerData])

    // Move success, updateSuccess, errorNotification inside useEffect or wrap with useCallback
    const success = useCallback(() => {
      messageApi.open({
        type: 'success',
        content: t('idea_list.idea_modal.action_idea_form.success_message'),
      })
    }, [messageApi, t])

    const updateSuccess = useCallback(() => {
      messageApi.open({
        type: 'success',
        content: t('idea_list.idea_modal.action_idea_form.update_message'),
      })
    }, [messageApi, t])

    const errorNotification = useCallback(
      (description: string) => {
        notificationApi.error({
          message: 'Error',
          description,
          placement: 'topRight',
        })
      },
      [notificationApi]
    )

    const onCloseForm = useCallback(() => {
      form.resetFields()
      setHorizonValue('')
      setConvictionValue('')
      setSelectedFigi('')
      setCompanyName('')
      onCancel()
    }, [form, onCancel])

    useEffect(() => {
      if (isSuccess) {
        success()
        onCloseForm()
        reset()
        if (onIdeaActioned) onIdeaActioned() // call after success
      }

      if (isUpdateSuccess) {
        updateSuccess()
        onCloseForm()
        updateReset()
        if (onIdeaActioned) onIdeaActioned()
      }
    }, [
      isSuccess,
      form,
      onCancel,
      reset,
      onIdeaActioned,
      isUpdateSuccess,
      updateReset,
      success,
      onCloseForm,
      updateSuccess,
    ])

    useEffect(() => {
      if (isError) {
        const description = getErrorDescription(error)
        errorNotification(description)
        reset()
      }

      if (isUpdateError) {
        updateReset()
      }
    }, [isError, error, reset, isUpdateError, updateReset, errorNotification])

    useEffect(() => {
      if (
        checkIdeaExistData &&
        checkIdeaExistData.data.exist &&
        checkIdeaExistData.data.ideaId
      ) {
        if (onEditIdea) {
          onEditIdea(checkIdeaExistData.data.ideaId)
        }
      }
    }, [checkIdeaExistData, checkIdeaExistLoading, onEditIdea])

    // Debounced search handler
    const handleTickerSearch = debounce((value: string) => {
      if (value && value.length >= 2) {
        fetchTickerSearch({ keyword: value })
      } else {
        setTickerOptions([])
      }
    }, 400)

    const handleDirectionChange = (val: IdeaDirectionType | '') => {
      form.setFieldsValue({
        direction: val,
      })
      setTimeout(() => setVisualDirection(val), THUMB_TRANSITION_MS)
    }

    // Single source of truth for the target market value ceiling.
    // Direct input, the %BP buttons and the "Maxfill will be" hint must all
    // derive from `maxTargetMarketValue` so their limits never diverge.
    const clampToMaxTargetMarketValue = (value: number) =>
      isGlobal ? value : Math.min(value, maxTargetMarketValue)

    const isOverMaxTargetMarketValue = (value: number) =>
      !isGlobal && value > maxTargetMarketValue

    const handleTargetMarketValueBp = (percentage: number) => () => {
      if (!buyingPower) return

      const calculatedValue = Math.round(buyingPower * percentage)
      if (!calculatedValue) return

      setIsMaxFillReached(isOverMaxTargetMarketValue(calculatedValue))
      form.setFieldsValue({
        targetMarketValue: clampToMaxTargetMarketValue(calculatedValue),
      })

      if (
        mode !== 'create' &&
        ideaDetail &&
        form.getFieldValue('direction') === IdeaDirectionType.Short &&
        isShortableData &&
        !isShortableData.data.isShortable &&
        form.getFieldValue('targetMarketValue') > ideaDetail.targetMarketValue
      ) {
        setShowCannotShortMessage(true)
      } else {
        setShowCannotShortMessage(false)
      }
    }

    const handleTargetPrice = (percentage: number) => () => {
      const price = tickerPriceData?.data?.price
      if (!price) return

      const direction = form.getFieldValue('direction')
      const multiplier =
        direction === IdeaDirectionType.Short ? 1 - percentage : 1 + percentage
      form.setFieldsValue({
        target: parseFloat((price * multiplier).toFixed(2)),
      })
    }

    const handleStopPrice = (percentage: number) => () => {
      const price = tickerPriceData?.data?.price
      if (!price) return

      const direction = form.getFieldValue('direction')
      const multiplier =
        direction === IdeaDirectionType.Short ? 1 + percentage : 1 - percentage
      form.setFieldsValue({
        stop: parseFloat((price * multiplier).toFixed(2)),
      })
    }

    const handleHorizonClick = (value: string) => {
      setHorizonValue(value)
      form.setFieldsValue({ horizon: value })
    }

    const handleConvictionClick = (value: string) => {
      setConvictionValue(value)
      form.setFieldsValue({ conviction: value })
    }

    // Set company name when ticker is selected and fetch ticker price
    const handleTickerSelect = (value: string) => {
      form.resetFields(['targetMarketValue', 'direction', 'target', 'stop'])
      const selected = tickerOptions.find((t) => t.value === value)
      let finalCompanyName = selected?.name ?? ''
      if (finalCompanyName && selected?.legalName) {
        finalCompanyName = finalCompanyName.concat(
          ' (' + selected?.legalName + ')'
        )
      }
      setCompanyName(finalCompanyName)
      if (selected?.value) {
        setSelectedFigi(selected.value)
      }
      if (selected?.bbgCompositeKey) {
        fetchTickerPrice({ bbgCompositeKey: selected.bbgCompositeKey })
        setSelectedBbgCompositeKey(selected?.bbgCompositeKey)
      }

      if (!isGlobal && buyingPower) {
        const maxUpSize =
          tickerData?.data?.tickers?.find(
            (t) => selected?.bbgCompositeKey === t.bbgCompositeKey
          )?.maxUpSize ?? maxTargetMarketValue
        setMaxTargetMarketValue(maxUpSize as number)

        const targetMarketValue = form.getFieldValue('targetMarketValue')

        if (maxUpSize && targetMarketValue > maxUpSize) {
          setIsMaxFillReached(true)
          form.setFieldsValue({ targetMarketValue: maxUpSize as number })
        } else {
          setIsMaxFillReached(false)
        }
      }
      if (visualDirection === '') {
        checkDirectionError(visualDirection)
      } else {
        setVisualDirection('')
      }
    }

    const onFinish: FormProps<CreateIdeaFormFieldTypes>['onFinish'] = (
      values
    ) => {
      newRelic.recordCustomEvent('FormAction', {
        element: NEW_RELIC.FORM_ACTION.submitCreateIdea,
        action: 'click',
      })
      createIdea({
        ...values,
        tradeReason: Array.isArray(values.tradeReason)
          ? values.tradeReason.join(',')
          : values.tradeReason,
      })
    }

    const watchedTargetMarketValue = Form.useWatch('targetMarketValue', form)
    const watchedTarget = Form.useWatch('target', form)
    const watchedStop = Form.useWatch('stop', form)
    const watchedConviction = Form.useWatch('conviction', form)
    const watchedHorizon = Form.useWatch('horizon', form)
    const watchedTradeReason = Form.useWatch('tradeReason', form) as
      | string
      | string[]
      | undefined
    const watchedFillStrategy = Form.useWatch('fillStrategy', form)
    const watchedKeepFilling = Form.useWatch('keepFilling', form)

    const hasEditedUpdateValues = useMemo(() => {
      if (mode !== 'edit' || !ideaDetail) return false

      const initialFillStrategy =
        ideaDetail.fillStrategy === '15min-VWAP' ||
        ideaDetail.fillStrategy === '1hour-VWAP'
          ? FillStrategies[0].value
          : ideaDetail.fillStrategy

      const initialTargetMarketValue = Math.round(ideaDetail.currentMarketValue)
      const initialTradeReason = normalizeTradeReasonValue(
        ideaDetail.tradeReason
      )

      return (
        (watchedTargetMarketValue ?? initialTargetMarketValue) !==
          initialTargetMarketValue ||
        (watchedTarget ?? ideaDetail.target) !== ideaDetail.target ||
        (watchedStop ?? ideaDetail.stop) !== ideaDetail.stop ||
        (watchedConviction ?? ideaDetail.conviction) !==
          ideaDetail.conviction ||
        (watchedHorizon ?? ideaDetail.horizon) !== ideaDetail.horizon ||
        normalizeTradeReasonValue(watchedTradeReason ?? initialTradeReason) !==
          initialTradeReason ||
        (watchedFillStrategy ?? initialFillStrategy) !== initialFillStrategy ||
        (watchedKeepFilling ?? ideaDetail.keepFilling) !==
          ideaDetail.keepFilling
      )
    }, [
      mode,
      ideaDetail,
      watchedTargetMarketValue,
      watchedTarget,
      watchedStop,
      watchedConviction,
      watchedHorizon,
      watchedTradeReason,
      watchedFillStrategy,
      watchedKeepFilling,
    ])

    const onUpdate: FormProps<CreateIdeaFormFieldTypes>['onFinish'] = async (
      values
    ) => {
      try {
        if (!hasEditedUpdateValues) return

        const tradeReason = Array.isArray(values.tradeReason)
          ? values.tradeReason.join(',')
          : values.tradeReason

        const payload: IdeaUpdateRequest = { id: ideaDetail?.id ?? '' }

        // Match the same fillStrategy mapping used when pre-filling edit form
        const initialFillStrategy =
          ideaDetail?.fillStrategy === '15min-VWAP' ||
          ideaDetail?.fillStrategy === '1hour-VWAP'
            ? FillStrategies[0].value
            : ideaDetail?.fillStrategy

        // Match the same targetMarketValue used when pre-filling edit form
        const initialTargetMarketValue = Math.round(
          ideaDetail?.currentMarketValue ?? 0
        )

        const hasFillStrategyChanged =
          values.fillStrategy !== initialFillStrategy
        const hasTargetMarketValueChanged =
          values.targetMarketValue !== initialTargetMarketValue

        if (hasFillStrategyChanged || hasTargetMarketValueChanged) {
          payload.targetMarketValue = values.targetMarketValue
        }

        payload.target = values.target
        payload.stop = values.stop
        payload.conviction = values.conviction
        payload.horizon = values.horizon
        payload.tradeReason = tradeReason
        payload.fillStrategy = values.fillStrategy
        payload.keepFilling = values.keepFilling

        await updateIdea(payload).unwrap()
        updateSuccess()
        onCloseForm()
      } catch (err) {
        const description = getErrorDescription(err)
        errorNotification(description)
      }
    }

    // Expose resetFormState to parent via ref
    useImperativeHandle(ref, () => ({
      resetFormState: () => {
        setHorizonValue('')
        setConvictionValue('')
        setSelectedFigi('')
        setCompanyName('')
      },
    }))

    const isMobile = useIsMobile(768)

    function checkDirectionError(currentDirection: IdeaDirectionType | '') {
      if (currentDirection || !form.getFieldValue('figi')) {
        setDirectionError('')
      } else {
        form.validateFields(['direction']).catch(({ errorFields }) => {
          const err = errorFields?.find(
            (f: { name: string[] }) => f.name[0] === 'direction'
          )

          if (err) setDirectionError(err.errors[0])
        })
      }
    }

    useEffect(() => {
      if (mode !== 'create') return
      checkDirectionError(visualDirection)
    }, [visualDirection])

    const canShort =
      mode !== 'edit' &&
      (!form.getFieldValue('figi') ||
        (!isShortableLoading && isShortableData?.data.isShortable))

    return (
      <>
        {messageContextHolder}
        {notificationContextHolder}
        <Form
          form={form}
          layout={'vertical'}
          onFinish={mode === 'create' ? onFinish : onUpdate}
          onFinishFailed={() => {
            checkDirectionError(visualDirection)
          }}
          initialValues={{
            targetMarketValue: 1000000,
          }}
        >
          <Row
            gutter={
              isMobile
                ? [0, 0]
                : [
                    convertPxToNumber(themeStyle.marginBase),
                    convertPxToNumber(themeStyle.marginBase),
                  ]
            }
            align={isMobile ? 'stretch' : 'middle'}
            style={{
              marginBottom: isMobile ? '' : themeStyle.marginSM,
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <Col
              span={isMobile ? 24 : 8}
              style={isMobile ? { marginBottom: 8 } : {}}
            >
              <Form.Item
                label={t('idea_list.idea_modal.action_idea_form.ticker')}
                name={'figi'}
                style={{ width: '100%' }}
                rules={[
                  {
                    required: true,
                    message: t(
                      'idea_list.idea_modal.action_idea_form.ticker_required'
                    ),
                  },
                  ...(mode === 'create'
                    ? [
                        {
                          validator: (_: RuleObject, value: string) => {
                            const tickerOption = tickerOptions.find(
                              (option) => option.value === value
                            )
                            if (value && tickerOption?.status === 'Inactive') {
                              return Promise.reject(
                                <InvalidTickerMessage
                                  message={t(
                                    'idea_list.idea_modal.action_idea_form.ticker_market_cap_adv_limit'
                                  )}
                                  ticker={tickerOption.label}
                                />
                              )
                            }
                            return Promise.resolve()
                          },
                        },
                      ]
                    : []),
                ]}
                help={(() => {
                  const currentValue = form.getFieldValue('figi')
                  const tickerOption = tickerOptions.find(
                    (option) => option.value === currentValue
                  )

                  // Show validation error first if ticker is inactive
                  if (
                    currentValue &&
                    tickerOption?.status === 'Inactive' &&
                    mode === 'create'
                  ) {
                    return (
                      <>
                        <InvalidTickerMessage
                          message={t(
                            'idea_list.idea_modal.action_idea_form.ticker_market_cap_adv_limit'
                          )}
                          ticker={tickerOption.label}
                        />
                      </>
                    )
                  }
                })()}
              >
                <Select
                  allowClear={mode === 'create'}
                  showSearch
                  placeholder={'Add Active Ticker'}
                  filterOption={false}
                  onSearch={mode === 'create' ? handleTickerSearch : undefined}
                  options={tickerOptions}
                  loading={tickerLoading}
                  notFoundContent={
                    tickerLoading ? 'Loading...' : 'No tickers found'
                  }
                  onSelect={mode === 'create' ? handleTickerSelect : undefined}
                  onClear={
                    mode === 'create'
                      ? () => {
                          setTickerOptions([])
                          setCompanyName('')
                        }
                      : undefined
                  }
                  disabled={mode == 'edit'}
                  dropdownStyle={
                    isMobile
                      ? { minWidth: 0, width: '90vw', maxWidth: '90vw' }
                      : {}
                  }
                  style={isMobile ? { width: '100%' } : {}}
                />
              </Form.Item>
            </Col>
            {companyName && (
              <Col style={{ marginTop: themeStyle.paddingBase }}>
                <IdeyaTypography.Text type={'secondary'}>
                  {companyName}
                </IdeyaTypography.Text>
              </Col>
            )}
          </Row>
          <Row
            style={{
              borderTop: isMobile ? '' : '1px solid rgba(0, 0, 0, 0.15)',
              borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <Col
              sm={12}
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: isMobile ? '12px 0px' : '12px 20px 12px 0px',
                borderRight: isMobile
                  ? undefined
                  : '1px solid rgba(0, 0, 0, 0.15)',
              }}
            >
              <Row
                align={'middle'}
                justify={'space-between'}
                style={{ marginBottom: directionError ? '0px' : '10px' }}
              >
                <Form.Item
                  name={'direction'}
                  style={{ marginBottom: '0px' }}
                  rules={[
                    {
                      required: true,
                      message: '⚠️ Please select trade side first',
                    },
                  ]}
                  help={''}
                  validateStatus={''}
                >
                  <Row gutter={[convertPxToNumber(themeStyle.marginXS), 0]}>
                    <Col>
                      <IdeyaButton
                        onClick={() =>
                          handleDirectionChange(IdeaDirectionType.Long)
                        }
                        disabled={mode === 'edit'}
                        style={
                          mode === 'edit'
                            ? {}
                            : {
                                borderColor: themeStyle.colorLongBorder,
                                backgroundColor:
                                  visualDirection === IdeaDirectionType.Long
                                    ? themeStyle.colorLong
                                    : themeStyle.colorLongBg,
                                color:
                                  visualDirection === IdeaDirectionType.Long
                                    ? token.colorTextLightSolid
                                    : themeStyle.colorLong,
                              }
                        }
                      >
                        {t('idea_list.idea_table.direction.long')}
                      </IdeyaButton>
                    </Col>
                    <Col>
                      <IdeyaButton
                        onClick={() =>
                          handleDirectionChange(IdeaDirectionType.Short)
                        }
                        disabled={!canShort}
                        style={
                          !canShort
                            ? {}
                            : {
                                borderColor: themeStyle.colorShortBorder,
                                backgroundColor:
                                  visualDirection === IdeaDirectionType.Short
                                    ? themeStyle.colorShort
                                    : themeStyle.colorShortBg,
                                color:
                                  visualDirection === IdeaDirectionType.Short
                                    ? token.colorTextLightSolid
                                    : themeStyle.colorShort,
                              }
                        }
                      >
                        {t('idea_list.idea_table.direction.short')}
                      </IdeyaButton>
                    </Col>
                  </Row>
                </Form.Item>
                <IdeyaTypography.Text>Unit : USD</IdeyaTypography.Text>
              </Row>
              {directionError && (
                <Row style={{ marginBottom: '10px' }}>
                  <IdeyaTypography.Text type={'danger'}>
                    {directionError}
                  </IdeyaTypography.Text>
                </Row>
              )}
              <Row
                style={{
                  flex: 1,
                  padding: isMobile ? 8 : '0px 1.25rem',
                  flexDirection: 'column',
                  justifyContent: 'space-evenly',
                  alignItems: 'flex-start',
                  alignSelf: 'stretch',
                  backgroundColor: targetSectionBgColor[visualDirection ?? ''],
                  borderRadius: '0.25rem',
                  ...(isMobile ? { gap: '1.25rem' } : undefined),
                }}
              >
                <div style={{ width: '100%' }}>
                  <Form.Item
                    className={'target-market-label-item'}
                    name={'targetMarketValue'}
                    style={{ width: '100%', marginBottom: '8px' }}
                    label={
                      <div
                        className={'custom-label'}
                        style={{
                          display: isMobile ? '' : 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                          minWidth: 0,
                        }}
                      >
                        <IdeyaTypography.Text
                          style={{ marginRight: isMobile ? '8px' : '' }}
                        >
                          {t(
                            'idea_list.idea_modal.action_idea_form.target_market_value'
                          )}
                        </IdeyaTypography.Text>
                        <IdeyaTypography.Text style={{ color: '#EB855D' }}>
                          {isMobile ? 'Available' : 'Available Capital'} :{' '}
                          {availableCapital}
                        </IdeyaTypography.Text>
                      </div>
                    }
                    help={
                      isMaxFillReached && !isGlobal ? (
                        <span style={{ color: themeStyle.agGridTableRed }}>
                          Maxfill will be $
                          {maxTargetMarketValue.toLocaleString()}
                        </span>
                      ) : null
                    }
                    rules={[
                      {
                        required: true,
                        message: t(
                          'idea_list.idea_modal.action_idea_form.target_market_value_required'
                        ),
                      },
                      ...(mode === 'edit'
                        ? [
                            {
                              validator: (_: RuleObject, value: unknown) => {
                                if (isGlobal || typeof value !== 'number') {
                                  return Promise.resolve()
                                }

                                if (value <= maxTargetMarketValue) {
                                  return Promise.resolve()
                                }

                                const isInitialEditValue =
                                  mode === 'edit' &&
                                  ideaDetail &&
                                  Math.round(ideaDetail.currentMarketValue) ===
                                    value

                                if (isInitialEditValue) {
                                  return Promise.resolve()
                                }

                                return Promise.reject(
                                  `${t(
                                    'idea_list.idea_modal.action_idea_form.target_market_value_cannot_exceed_max_value'
                                  )}: ${maxTargetMarketValue.toLocaleString()}`
                                )
                              },
                            },
                          ]
                        : []),
                    ]}
                  >
                    <InputNumber<number>
                      controls={false}
                      style={{ width: '100%' }}
                      formatter={(value) =>
                        `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={(value) =>
                        value?.replace(/\$\s?|(,*)/g, '') as unknown as number
                      }
                      precision={0}
                      onChange={(value) => {
                        if (!value) {
                          setIsMaxFillReached(false)
                          setShowCannotShortMessage(false)
                          return
                        }

                        setIsMaxFillReached(isOverMaxTargetMarketValue(value))

                        if (
                          mode !== 'create' &&
                          ideaDetail &&
                          form.getFieldValue('direction') ===
                            IdeaDirectionType.Short &&
                          isShortableData &&
                          !isShortableData.data.isShortable &&
                          form.getFieldValue('targetMarketValue') >
                            ideaDetail.targetMarketValue
                        ) {
                          setShowCannotShortMessage(true)
                        } else {
                          setShowCannotShortMessage(false)
                        }
                      }}
                      max={isGlobal ? undefined : maxTargetMarketValue}
                      min={MinTargetMarketValue}
                      step={TargetMarketValueStep}
                    />
                  </Form.Item>
                  {showCannotShortMessage && (
                    <span style={{ color: themeStyle.agGridTableRed }}>
                      {t(
                        'idea_list.idea_modal.action_idea_form.ticker_cannot_short_anymore'
                      )}
                    </span>
                  )}
                  <div
                    style={{
                      width: '100%',
                    }}
                  >
                    <Row
                      gutter={[
                        convertPxToNumber(themeStyle.marginXS),
                        convertPxToNumber(themeStyle.marginXS),
                      ]}
                      justify={isMobile ? 'space-between' : undefined}
                      style={{
                        flexWrap: 'wrap',
                        width: '100%',
                      }}
                    >
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetMarketValueBp(
                            isJpOrGlobal ? 0.01 : 0.02
                          )}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          {isJpOrGlobal ? '1' : '2'}% BP
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetMarketValueBp(
                            isJpOrGlobal ? 0.02 : 0.04
                          )}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          {isJpOrGlobal ? '2' : '4'}% BP
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetMarketValueBp(
                            isJpOrGlobal ? 0.03 : 0.06
                          )}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          {isJpOrGlobal ? '3' : '6'}% BP
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetMarketValueBp(
                            isJpOrGlobal ? 0.04 : 0.08
                          )}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          {isJpOrGlobal ? '4' : '8'}% BP
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetMarketValueBp(
                            isJpOrGlobal ? 0.05 : 0.1
                          )}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          {isJpOrGlobal ? '5' : '10'}% BP
                        </IdeyaButton>
                      </Col>
                    </Row>
                  </div>
                </div>
                <div style={{ width: '100%' }}>
                  <Form.Item
                    name={'target'}
                    style={{ width: '100%', marginBottom: '8px' }}
                    label={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                          minWidth: 0,
                        }}
                      >
                        <IdeyaTypography.Text>
                          {t(
                            'idea_list.idea_modal.action_idea_form.target_price'
                          )}
                        </IdeyaTypography.Text>
                      </div>
                    }
                    rules={[
                      {
                        required: true,
                        message: t(
                          'idea_list.idea_modal.action_idea_form.target_price_required'
                        ),
                      },
                      {
                        validator: (_, value) => {
                          const direction = form.getFieldValue('direction')
                          if (
                            direction === IdeaDirectionType.Short &&
                            tickerPriceData &&
                            value > tickerPriceData.data.price
                          ) {
                            return Promise.reject(
                              new Error(
                                t(
                                  'idea_list.idea_modal.action_idea_form.price_above'
                                )
                              )
                            )
                          } else if (
                            direction === IdeaDirectionType.Long &&
                            tickerPriceData &&
                            value < tickerPriceData.data.price
                          ) {
                            return Promise.reject(
                              new Error(
                                t(
                                  'idea_list.idea_modal.action_idea_form.price_below'
                                )
                              )
                            )
                          }
                          return Promise.resolve()
                        },
                      },
                    ]}
                  >
                    <InputNumber<number>
                      min={0}
                      controls={false}
                      precision={2}
                      style={{ width: '100%' }}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={(value) =>
                        value?.replace(/\$\s?|(,*)/g, '') as unknown as number
                      }
                    />
                  </Form.Item>
                  <div style={{ width: '100%' }}>
                    <Row
                      gutter={[
                        convertPxToNumber(themeStyle.marginXS),
                        convertPxToNumber(themeStyle.marginXS),
                      ]}
                      justify={isMobile ? 'space-between' : undefined}
                      style={{ flexWrap: 'wrap', width: '100%' }}
                    >
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetPrice(0.1)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          10%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetPrice(0.2)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          20%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetPrice(0.3)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          30%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetPrice(0.4)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          40%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetPrice(0.5)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          50%
                        </IdeyaButton>
                      </Col>
                    </Row>
                  </div>
                </div>
                <div style={{ width: '100%' }}>
                  <Form.Item
                    name={'stop'}
                    style={{ width: '100%', marginBottom: '8px' }}
                    label={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                          minWidth: 0,
                        }}
                      >
                        <IdeyaTypography.Text>
                          {t(
                            'idea_list.idea_modal.action_idea_form.stop_price'
                          )}
                        </IdeyaTypography.Text>
                      </div>
                    }
                    rules={[
                      {
                        required: true,
                        message: t(
                          'idea_list.idea_modal.action_idea_form.stop_price_required'
                        ),
                      },
                      {
                        validator: (_, value) => {
                          const direction = form.getFieldValue('direction')
                          if (
                            direction === IdeaDirectionType.Short &&
                            tickerPriceData &&
                            value < tickerPriceData.data.price
                          ) {
                            return Promise.reject(
                              new Error(
                                t(
                                  'idea_list.idea_modal.action_idea_form.price_below'
                                )
                              )
                            )
                          } else if (
                            direction === IdeaDirectionType.Long &&
                            tickerPriceData &&
                            value > tickerPriceData.data.price
                          ) {
                            return Promise.reject(
                              new Error(
                                t(
                                  'idea_list.idea_modal.action_idea_form.price_above'
                                )
                              )
                            )
                          }
                          return Promise.resolve()
                        },
                      },
                    ]}
                  >
                    <InputNumber<number>
                      controls={false}
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={(value) =>
                        value?.replace(/\$\s?|(,*)/g, '') as unknown as number
                      }
                    />
                  </Form.Item>
                  <div style={{ width: '100%' }}>
                    <Row
                      gutter={[
                        convertPxToNumber(themeStyle.marginXS),
                        convertPxToNumber(themeStyle.marginXS),
                      ]}
                      justify={isMobile ? 'space-between' : undefined}
                      style={{ flexWrap: 'wrap', width: '100%' }}
                    >
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleStopPrice(0.05)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          5%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleStopPrice(0.1)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          10%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleStopPrice(0.15)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          15%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleStopPrice(0.2)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          20%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleStopPrice(0.3)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          30%
                        </IdeyaButton>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Row>
            </Col>
            <Col
              sm={12}
              style={{
                width: '100%',
                padding: isMobile ? '12px 0px' : '12px 0px 12px 20px',
              }}
            >
              <Row
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  alignSelf: 'stretch',
                  gap: '20px',
                }}
              >
                <Form.Item
                  name={'fillStrategy'}
                  label={t(
                    'idea_list.idea_modal.action_idea_form.fill_strategy'
                  )}
                  style={{ width: '100%', marginBottom: '0px' }}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'idea_list.idea_modal.action_idea_form.fill_strategy_required'
                      ),
                    },
                  ]}
                >
                  <Radio.Group
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    disabled={
                      fillStrategyLoading ||
                      (mode === 'edit' &&
                        fillStrategyData?.data.hasUnfilledAction &&
                        fillStrategyData?.data.canFill)
                    }
                    onChange={() => {
                      form.setFieldsValue({
                        keepFilling: true,
                      })
                    }}
                  >
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.fillStrategy !== currentValues.fillStrategy
                      }
                    >
                      {({ getFieldValue }) => (
                        <div
                          style={{
                            backgroundColor:
                              getFieldValue('fillStrategy') ===
                              FillStrategies[0].value
                                ? themeStyle.fillStrategyBackground
                                : 'transparent',
                            display: 'flex',
                            alignItems: 'flex-start',
                            padding: themeStyle.paddingXS,
                            borderRadius: themeStyle.borderRadiusBase,
                          }}
                        >
                          <Radio value={FillStrategies[0].value}>
                            <IdeyaTypography.Text>
                              {FillStrategies[0].label}
                            </IdeyaTypography.Text>
                            {getFieldValue('fillStrategy') ===
                              FillStrategies[0].value && (
                              <>
                                {' '}
                                <IdeyaTypography.Text type={'danger'}>
                                  ⚠️ This cannot be reversed once submitted.
                                </IdeyaTypography.Text>
                              </>
                            )}
                          </Radio>
                        </div>
                      )}
                    </Form.Item>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        borderRadius: themeStyle.borderRadiusBase,
                      }}
                    >
                      <Radio value={FillStrategies[1].value}>
                        {FillStrategies[1].label}
                      </Radio>
                    </div>
                  </Radio.Group>
                </Form.Item>
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => {
                    const fillStrategy = getFieldValue('fillStrategy')
                    return (
                      <Row
                        gutter={[
                          convertPxToNumber(themeStyle.marginSM),
                          isMobile ? convertPxToNumber(themeStyle.marginSM) : 0,
                        ]}
                        style={{
                          width: '100%',
                          backgroundColor: themeStyle.colorFillTertiary,
                          borderRadius: themeStyle.borderRadiusBase,
                          padding: themeStyle.marginSM,
                        }}
                      >
                        <Col md={{ flex: '0 0 40px' }}>
                          <Form.Item name={'keepFilling'} noStyle>
                            <Switch
                              disabled={!fillStrategy}
                              style={{ margin: 0 }}
                            />
                          </Form.Item>
                        </Col>
                        <Col md={{ flex: 1 }}>
                          <IdeyaTypography.Text
                            style={{
                              color: fillStrategy
                                ? undefined
                                : themeStyle.colorTextDisabled,
                            }}
                          >
                            Auto Fill Residual Order
                          </IdeyaTypography.Text>
                        </Col>
                        {fillStrategy && (
                          <Col>
                            <div style={{ marginLeft: isMobile ? 0 : '40px' }}>
                              <IdeyaTypography.Text
                                style={{
                                  color: themeStyle.colorTextDisabled,
                                }}
                              >
                                {fillStrategy === FillStrategies[0].value &&
                                  'Residual order after 1 hour VWAP will be automatically filled with EOD VWAP'}

                                {fillStrategy === FillStrategies[1].value &&
                                  'Residual order after VWAP till EOD will be automatically carried on to the next day'}
                              </IdeyaTypography.Text>
                            </div>
                          </Col>
                        )}
                      </Row>
                    )
                  }}
                </Form.Item>
                {!isMobile && (
                  <Form.Item
                    name={'tradeReason'}
                    label={t(
                      'idea_list.idea_modal.action_idea_form.trade_reason'
                    )}
                    style={{ width: '100%', marginBottom: '0px' }}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'idea_list.idea_modal.action_idea_form.trade_reason_required'
                        ),
                      },
                    ]}
                  >
                    <TreeSelect
                      showSearch
                      style={{ width: '100%' }}
                      placeholder={'Select trade reason'}
                      allowClear
                      multiple
                      treeDefaultExpandAll
                      treeData={TradeReasonsTreeData}
                      treeCheckable={true}
                      listHeight={isMobile ? 300 : 500}
                      dropdownStyle={
                        isMobile
                          ? { minWidth: 0, width: '90vw', maxWidth: '90vw' }
                          : {}
                      }
                    />
                  </Form.Item>
                )}
                <Form.Item
                  name={'horizon'}
                  label={t('idea_list.idea_modal.action_idea_form.horizon')}
                  style={{ width: '100%', marginBottom: '0px' }}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'idea_list.idea_modal.action_idea_form.horizon_required'
                      ),
                    },
                  ]}
                >
                  <div style={{ width: '100%' }}>
                    <Row
                      gutter={[
                        convertPxToNumber(themeStyle.marginXS),
                        convertPxToNumber(themeStyle.marginXS),
                      ]}
                      style={{ flexWrap: 'wrap', width: '100%' }}
                    >
                      {[
                        'Intraday',
                        '1-4 Days',
                        '1-2 Weeks',
                        '2-4 Weeks',
                        '1-3 Months',
                        'Quarter',
                      ].map((value) => (
                        <Col key={value}>
                          <IdeyaButton
                            ideyaType={horizonValue === value ? '' : 'antd'}
                            onClick={() => handleHorizonClick(value)}
                            style={{
                              justifyContent: 'center',
                              display: 'flex',
                              borderColor: themeStyle.colorSplit,
                              width: isMobile ? '150px' : '',
                            }}
                          >
                            {value}
                          </IdeyaButton>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Form.Item>
                <Form.Item
                  name={'conviction'}
                  label={t('idea_list.idea_modal.action_idea_form.conviction')}
                  style={{ width: '100%', marginBottom: '0px' }}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'idea_list.idea_modal.action_idea_form.conviction_required'
                      ),
                    },
                  ]}
                >
                  <div style={{ width: '100%' }}>
                    <Row
                      gutter={[
                        convertPxToNumber(themeStyle.marginXS),
                        convertPxToNumber(themeStyle.marginXS),
                      ]}
                      style={{ flexWrap: 'wrap', width: '100%' }}
                    >
                      {['Very High', 'High', 'Medium', 'Low'].map((value) => (
                        <Col key={value}>
                          <IdeyaButton
                            ideyaType={convictionValue === value ? '' : 'antd'}
                            onClick={() => handleConvictionClick(value)}
                            style={{
                              justifyContent: 'center',
                              display: 'flex',
                              borderColor: themeStyle.colorSplit,
                              width: isMobile ? '150px' : '',
                            }}
                          >
                            {value}
                          </IdeyaButton>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Form.Item>
                {isMobile && (
                  <>
                    <Form.Item
                      name={'tradeReason'}
                      label={t(
                        'idea_list.idea_modal.action_idea_form.trade_reason'
                      )}
                      style={{ width: '100%', marginBottom: '0px' }}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'idea_list.idea_modal.action_idea_form.trade_reason_required'
                          ),
                        },
                      ]}
                    >
                      <Checkbox.Group
                        style={{ width: '100%' }}
                        onChange={(checkedValues) =>
                          setSelectedTradeReasons(checkedValues as string[])
                        }
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                          }}
                        >
                          {TradeReasonsTreeData.map((category) => (
                            <div key={category.value}>
                              {category.children ? (
                                <>
                                  <IdeyaTypography.Text type={'secondary'}>
                                    {category.title}
                                  </IdeyaTypography.Text>
                                  {category.children.map((child) => (
                                    <div
                                      style={{
                                        borderColor:
                                          selectedTradeReasons.includes(
                                            child.value
                                          )
                                            ? themeStyle.colorSplit
                                            : themeStyle.colorBorder,
                                        borderRadius: `${themeStyle.borderRadiusBase}`,
                                        border: `1px solid ${themeStyle.colorBorder}`,
                                        padding: `6px 15px`,
                                        marginBottom: '8px',
                                        backgroundColor:
                                          selectedTradeReasons.includes(
                                            child.value
                                          )
                                            ? themeStyle.blue6
                                            : '',
                                      }}
                                    >
                                      <Checkbox
                                        key={child.value}
                                        value={child.value}
                                        style={{
                                          color: selectedTradeReasons.includes(
                                            child.value
                                          )
                                            ? themeStyle.blue9
                                            : themeStyle.colorText,
                                        }}
                                      >
                                        {child.title}
                                      </Checkbox>
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <div
                                  style={{
                                    borderColor: selectedTradeReasons.includes(
                                      category.value
                                    )
                                      ? themeStyle.colorSplit
                                      : themeStyle.colorBorder,
                                    borderRadius: `${themeStyle.borderRadiusBase}`,
                                    border: `1px solid ${themeStyle.colorBorder}`,
                                    padding: `6px 15px`,
                                    backgroundColor:
                                      selectedTradeReasons.includes(
                                        category.value
                                      )
                                        ? themeStyle.blue6
                                        : '',
                                  }}
                                >
                                  <Checkbox
                                    value={category.value}
                                    style={{
                                      color: selectedTradeReasons.includes(
                                        category.value
                                      )
                                        ? themeStyle.blue9
                                        : themeStyle.colorText,
                                    }}
                                  >
                                    {category.title}
                                  </Checkbox>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </Checkbox.Group>
                    </Form.Item>
                  </>
                )}
                {/*
                // Hiding for now until compliance gives ok
                <Form.Item
                  label={t('idea_list.idea_modal.action_idea_form.comment')}
                  name={'comment'}
                  style={{ width: '100%' }}
                >
                  <Input.TextArea
                    rows={isMobile ? 3 : 1}
                    style={{ width: '100%' }}
                    placeholder={t(
                      'idea_list.idea_modal.action_idea_form.add_comment'
                    )}
                  />
                </Form.Item>*/}
              </Row>
            </Col>
          </Row>
          <Row
            justify={'space-between'}
            style={{
              padding: isMobile ? '12px 0px 0px 0px' : `12px 0px 0px 0px`,
              flexDirection: 'row',
              gap: isMobile ? 8 : 0,
            }}
          >
            <Col style={isMobile ? { marginBottom: 8 } : {}}>
              <IdeyaButton
                type={'text'}
                onClick={() => {
                  form.resetFields()
                  setHorizonValue('')
                  setConvictionValue('')
                  setCompanyName('')
                  setIsMaxFillReached(false)
                  setVisualDirection('')
                }}
                disabled={mode === 'edit'}
                style={{
                  color: themeStyle.colorPrimaryText,
                }}
              >
                {t('idea_list.idea_modal.action_idea_form.clear_form')}
              </IdeyaButton>
            </Col>
            <Col>
              <Row gutter={[8, 8]}>
                {mode === 'create' ? (
                  <>
                    <Col>
                      <IdeyaButton ghost onClick={onCloseForm}>
                        {t('common.cancel')}
                      </IdeyaButton>
                    </Col>
                    <Col>
                      <IdeyaButton
                        loading={isCreating}
                        disabled={isCreating}
                        htmlType={'submit'}
                      >
                        {t('common.create_idea')}
                      </IdeyaButton>
                    </Col>
                  </>
                ) : (
                  <>
                    <Col>
                      <IdeyaButton
                        type={'primary'}
                        danger
                        onClick={onIdeaLiquidated}
                      >
                        {t('idea_list.idea_modal.action_idea_form.liquidate')}
                      </IdeyaButton>
                    </Col>
                    <Col>
                      <IdeyaButton
                        onClick={form.submit}
                        loading={isUpdateLoading}
                        disabled={isUpdateLoading || !hasEditedUpdateValues}
                      >
                        {t('idea_list.idea_modal.action_idea_form.update')}
                      </IdeyaButton>
                    </Col>
                  </>
                )}
              </Row>
            </Col>
          </Row>
        </Form>
      </>
    )
  }
)
